// src/sim/knockdown.ts — Hitstun / Launched / Tumble / Knockdown / Lying / Roll / Crawl / GetUp zinciri (DESIGN §5–§6; ARCHITECTURE §3 adım 2,
// §4.4 adım 5/7). ADIM 2'de (stepTimers'tan hemen sonra) çağrılır: durum bitişleri, tumble kontrol dönüşü (Ev.TumbleControl + zıplama
// tamponu temizliği), Flying Kick whiff zinciri (Death_B 13 t → LyingFront 24 t → otomatik hızlı kalk 28 t, sonrası i-frame yok), yere kayma
// (τ 0.36 s: her tick ×0.955, |v| < 0.3 → 0) ve Lying girdileri (Roll / Crawl / hızlı kalk / kalkış tekmesi / B eşya). three'siz, tahsissiz.
//
// Durum sözleşmesi (WP2 ile aynı): stateTick = giriş, stateUntil = ÖZEL bitiş. Roll: TEK kaynak buradaki `startRoll` (dodge.ts'teki kopya kaldırıldı)
// (state Roll, stateUntil = tick + 21 + 1, invulnUntil = stateUntil, dodgeDir, rollUsed, vel 0, Ev.Roll) — hareketi fighter.ts ROLL_STEP verir;
// Roll bitişini fighter.stepTimers Lying'e (lyingUntil) döndürür. Kalkış tekmesi: `f.getUpKick = true` bayrağı → aynı tick adım 4'te
// moves.ts K_getup_kick'i başlatır (knockdown.ts moves.ts'i import etmez; döngü yok).
// lyingPlan / lyingSince artık Fighter alanı (eskiden modül dizisiydi): süreç-global durum iki dünyayı aynı süreçte koşturmayı imkânsız kılıyordu (NETWORK.md §5).
import { Btn, Ev, FState, HitKind } from '../core/types.ts';
import type { Fighter, SimWorld } from '../core/types.ts';
import { clamp01, degToRad, lerp, tickDecay } from '../core/math.ts';
import { leftX, leftZ } from '../core/vec3.ts';
import { NO_TICK, TICK_DT } from '../config/constants.ts';
import { btnIndex } from '../config/controls.ts';
import { T } from '../config/tuning.ts';

/** 2B uzunluk: Math.hypot V8'de FixedDoubleArray tahsis eder (sıcak yol yasağı; match.ts kalıbı) → sqrt(a²+b²). */
const hyp = (a: number, b: number): number => Math.sqrt(a * a + b * b);

const SLIDE_K = tickDecay(T.KNOCKDOWN.slideTau);   // ×0.955 / tick
const TUMBLE_STEP = T.KNOCKDOWN.tumbleTurnsPerSec * Math.PI * 2 * TICK_DT;
/** SQUASH_HIT = 3 (fighter.ts sabiti; view FEEL.squashHit'e eşler — paket dosyası import edilmez, sayı yerel). */
const SQUASH_HIT = 3, SQUASH_THROWN = 4;

function setState(f: Fighter, s: FState, tick: number, until: number = NO_TICK): void { f.state = s; f.stateTick = tick; f.stateUntil = until; }
/** Hamle alanlarını temizler (durum kesildi). */
export function clearMove(f: Fighter): void {
  f.moveIdx = -1; f.moveTick = 0; f.phase = 0; f.stageIdx = 0; f.stageStartTick = NO_TICK;
  f.touched = false; f.whiffed = false; f.firstHitTick = NO_TICK; f.hitMask.fill(0); f.rmDx = 0; f.rmDz = 0; f.rootFrame = -1;
  f.blocking = false; f.armorUntil = NO_TICK;
}

// ───────────────────────────── girişler ─────────────────────────────
/** Hitstun: Hit_A / Hit_B (kind), ticks sonra Idle/Air. */
export function enterHitstun(world: SimWorld, f: Fighter, ticks: number, kind: HitKind): void {
  const tick = world.tick;
  clearMove(f);
  f.hitstunTicks = ticks; f.lastHitKind = kind;
  setState(f, FState.Hitstun, tick, tick + ticks);
}
/** Launched: havada isabet (tumble değil) — hitstun bitince kontrol havada döner (Air). */
export function enterLaunched(world: SimWorld, f: Fighter, ticks: number): void {
  const tick = world.tick;
  clearMove(f);
  f.hitstunTicks = ticks; f.lastHitKind = HitKind.Mid;
  // DESIGN §5: root pitch −15°…−50°, savrulma hızına göre (yükseğe kalkan daha çok arkaya yatar). `vel.y` bu noktada
  // ZATEN darbe sonrası (hits.ts lift'i enterLaunched'dan önce yazar), yani eğim vuruşun sertliğini gösterir.
  // RADYAN saklanır — animMap `radToDeg` ile okuyor. (Hata: eskiden dereceyi doğrudan yazıyordu; radToDeg(−15) = −859°
  // ≡ −139°, karakter havada darbe yiyince tek karede baş aşağı dönüyordu — kullanıcı bulgusu "çok keskin".)
  f.launchPitch = degToRad(lerp(T.ANIM.launchPitchMin, T.ANIM.launchPitchMax, clamp01(f.vel.y / T.JUMP.v0)));
  setState(f, FState.Launched, tick, tick + ticks);
}
/** Tumble: KB'ye dik eksen etrafında dönüş; hitstun bitince tumbleControl; inişte Knockdown → Lying (84 t; fırlatılma 60 t).
 *  KB yönü f.lastHitDirX/Z'den (çağıran önce yazar): double argüman geçişi V8'de HeapNumber kutuluyordu (düzeltici tur 2). */
export function enterTumble(world: SimWorld, f: Fighter, hitstunTicks: number, thrown: boolean): void {
  const tick = world.tick;
  const dirX = f.lastHitDirX, dirZ = f.lastHitDirZ;
  clearMove(f);
  f.hitstunTicks = hitstunTicks; f.lastHitKind = thrown ? HitKind.Finisher : HitKind.Tumble;
  f.tumbleAxisX = dirZ; f.tumbleAxisZ = -dirX; f.tumbleAngle = 0; f.tumbleControl = false;
  f.faceDown = false;
  f.lyingPlan = thrown ? T.KNOCKDOWN.lyingThrownTicks : T.KNOCKDOWN.lyingTicks;
  if (thrown) { f.doubleJumpLeft = 0; f.airDodgeLeft = 0; f.squashKind = SQUASH_THROWN; f.squashUntil = tick + hitstunTicks; }   // "kader atıldığı anda belli"
  setState(f, FState.Tumble, tick, tick + hitstunTicks);
  world.events.push(Ev.Tumble, tick, f.id);
}
/**
 * Knockdown (yere çakılma): Death_A@0.37-0.55 tS 1.0 = 11 t; hitstop 7 t; silah düşer; kayma sürer → Lying.
 *
 * `lyingTicks` AÇIKÇA veriliyor (varsayılan DESIGN §6 tabanı). NEDEN: `f.lyingPlan` eskiden yalnız `enterTumble`
 * ve `enterFaceplant` tarafından yazılıyordu, ama `enterKnockdown` bir de DOĞRUDAN çağrılıyor (`hits.ts`, taklasız
 * knockdown). O yolda plan hiç yazılmadığı için BAYAT kalıyordu: bir kez yüzüstü düşen (whiff zinciri, 24 t) ya da
 * fırlatılan (60 t) dövüşçü, sonraki normal knockdown'da 84 t yerine o eski süreyi yatıyordu. Plan artık her
 * girişte açıkça belirleniyor; taşınan bir değer kalmadı.
 */
export function enterKnockdown(world: SimWorld, f: Fighter, faceDown: boolean = false,
                               lyingTicks: number = T.KNOCKDOWN.lyingTicks): void {
  const tick = world.tick;
  clearMove(f);
  f.lyingPlan = lyingTicks;
  f.faceDown = faceDown; f.tumbleControl = false; f.tumbleAngle = 0;
  f.vel.y = 0;
  const ticks = faceDown ? T.KNOCKDOWN.faceplantTicks : T.KNOCKDOWN.fallTicks;
  const stop = faceDown ? 0 : T.HITSTOP.finisher;
  setState(f, FState.Knockdown, tick, tick + ticks + stop);   // çakılma hitstop'u (7 t) süreye eklenir: donma süre yemez
  f.hitstop = Math.max(f.hitstop, stop);
  f.squashKind = SQUASH_HIT; f.squashUntil = tick + 5;
  world.systems.items.drop(world, f, tick, Ev.Knockdown);
  const e = world.events.push(Ev.Knockdown, tick, f.id); e.v0 = faceDown ? 1 : 0;
  e.x = f.pos.x; e.y = f.pos.y; e.z = f.pos.z;
}
/** Lying: i-frame yok, stun sönmez, kayma; Roll 1×; süre sonunda GetUp (yüzüstü: otomatik hızlı kalk, sonrası i-frame yok). */
export function enterLying(world: SimWorld, f: Fighter, ticks: number, faceDown: boolean): void {
  const tick = world.tick;
  clearMove(f);
  f.faceDown = faceDown; f.rollUsed = false; f.getUpKick = false; f.tumbleControl = false;
  f.lyingUntil = tick + ticks;
  f.lyingSince = tick;
  setState(f, FState.Lying, tick, f.lyingUntil);
  const e = world.events.push(Ev.Lying, tick, f.id); e.v0 = faceDown ? 1 : 0; e.v1 = ticks;
}
/** Flying Kick whiff zinciri: Death_B 13 t (Knockdown, yüzüstü) → LyingFront 24 t → otomatik hızlı kalk 28 t (i-frame yalnız kalkışta). */
export function enterFaceplant(world: SimWorld, f: Fighter): void {
  f.vel.x = 0; f.vel.z = 0;
  enterKnockdown(world, f, true, T.KNOCKDOWN.whiffLyingTicks);
}
/** GetUp: normal 48 t (i-frame tamamı + 36 t sonrası) / hızlı 28 t (A; whiff zincirinde otomatik ve sonrası i-frame yok). Kalkınca stun 0. */
export function enterGetUp(world: SimWorld, f: Fighter, quick: boolean, postIframes: number): void {
  const tick = world.tick;
  clearMove(f);
  const ticks = quick ? T.KNOCKDOWN.quickGetUpTicks : T.KNOCKDOWN.getUpTicks;
  f.rollUsed = false; f.getUpKick = false;
  f.vel.x = 0; f.vel.z = 0;
  setState(f, FState.GetUp, tick, tick + ticks);
  f.invulnUntil = tick + ticks + postIframes;
  const e = world.events.push(Ev.GetUp, tick, f.id); e.v0 = quick ? 1 : 0; e.v1 = f.faceDown ? 1 : 0;
}

/** Yuvarlanma (tek kaynak; lyingInputs çağırır): 1.3 u yana 21 t, i-frame tamamı (vuruş + kaldırma), 1×/knockdown; hareket fighter.ts'te. */
export function startRoll(world: SimWorld, f: Fighter, dirX: number, dirZ: number): boolean {
  if (f.rollUsed || (f.state !== FState.Lying && f.state !== FState.Crawl)) return false;
  const tick = world.tick;
  const m = hyp(dirX, dirZ);
  if (m < 1e-6) return false;
  f.dodgeDirX = dirX / m; f.dodgeDirZ = dirZ / m;
  f.rollUsed = true;
  f.vel.x = 0; f.vel.z = 0;
  setState(f, FState.Roll, tick, tick + T.KNOCKDOWN.rollTicks + 1);
  f.invulnUntil = f.stateUntil;
  const e = world.events.push(Ev.Roll, tick, f.id); e.dx = f.dodgeDirX; e.dz = f.dodgeDirZ;
  return true;
}

// ───────────────────────────── yardımcılar ─────────────────────────────
/** DESIGN §6: çakılma hitstop'u "yalnız kurban + vuran" — Tumble inişinde kombo penceresi (84 t) içindeki son vuran da donar (knockdown
 *  üçlüsü okunurluğu; Attack'taysa moveTick durur, ek işlem yok). Doğrudan isabetle gelen knockdown'da hits.ts saldıranı zaten dondurur
 *  (orada lastHitBy henüz güncellenmemiştir → yalnız iniş yolunda çağrılır; üçüncü kişi yanlışlıkla donmaz). */
function freezeLastAttacker(world: SimWorld, f: Fighter, stop: number): void {
  if (f.lastHitBy < 0 || f.lastHitBy === f.id || f.lastHitTick === NO_TICK) return;
  if (world.tick - f.lastHitTick > T.COMBAT.comboWindowTicks) return;
  const a = world.fighters[f.lastHitBy];
  if (!a || a.hitstop >= stop) return;
  a.hitstop = stop;
  const h = world.events.push(Ev.Hitstop, world.tick, a.id, f.id); h.v0 = stop;
}
function slide(f: Fighter): void {
  f.vel.x *= SLIDE_K; f.vel.z *= SLIDE_K;
  if (hyp(f.vel.x, f.vel.z) < T.KNOCKDOWN.slideStop) { f.vel.x = 0; f.vel.z = 0; }
}
function pressedWithin(world: SimWorld, f: Fighter, btn: Btn, window: number): boolean {
  const t = world.buffers[f.id]!.pressTick[btnIndex(btn)]!;
  return t !== NO_TICK && world.tick - t <= window;
}
function consume(world: SimWorld, f: Fighter, btn: Btn): void { world.buffers[f.id]!.pressTick[btnIndex(btn)] = NO_TICK; }
function toIdleOrAir(f: Fighter, tick: number): void { setState(f, f.grounded ? FState.Idle : FState.Air, tick); }

/** Lying'e giriş tick'i (hızlı kalk ≥ 30 t kuralı ve bot için). */
export const lyingSinceOf = (f: Fighter): number => f.lyingSince;

/** Lying / Crawl ortak girdileri: A hızlı kalk (≥ 30 t), Y kalkış tekmesi, R1 yuvarlan, çubuk sürün, B eşya. */
function lyingInputs(world: SimWorld, f: Fighter): void {
  const tick = world.tick;
  if (f.controlLockUntil > tick) return;
  const input = world.inputs[f.id]!;
  const mag = hyp(input.moveX, input.moveZ);
  // Y → kalkış tekmesi (bayrak; moves.ts aynı tick'te K_getup_kick'i başlatır)
  if (pressedWithin(world, f, Btn.Heavy, T.INPUT.bufferTicks) && f.state === FState.Lying) {
    consume(world, f, Btn.Heavy); f.getUpKick = true; return;
  }
  // A → hızlı kalk (Lying'in ≥ 30. t'i)
  if (pressedWithin(world, f, Btn.Jump, T.JUMP.bufferTicks) && tick - f.lyingSince >= T.KNOCKDOWN.quickGetUpAfter) {
    consume(world, f, Btn.Jump);
    enterGetUp(world, f, true, f.faceDown ? T.KNOCKDOWN.whiffPostGetUpIframes : T.KNOCKDOWN.postGetUpIframes);
    return;
  }
  // R1 → yuvarlan (çubuk yönü; nötr = sola)
  if (pressedWithin(world, f, Btn.Dodge, T.INPUT.bufferTicks) && !f.rollUsed) {
    consume(world, f, Btn.Dodge);
    if (mag > T.MOVE.walkDead) startRoll(world, f, input.moveX, input.moveZ);
    else startRoll(world, f, leftX(f.yaw), leftZ(f.yaw));
    return;
  }
  // B → eşya al (sürünerek; items.ts Lying/Crawl'da durumu değiştirmez)
  if (pressedWithin(world, f, Btn.Grab, T.INPUT.grabBufferTicks)) {
    if (world.systems.items.tryPickup(world, f, tick)) consume(world, f, Btn.Grab);
  }
  // çubuk → sürün / bırakınca yat (Lying süresi sayaçtan bağımsız sürer)
  if (f.state === FState.Lying && mag > T.MOVE.walkDead) setState(f, FState.Crawl, tick, f.lyingUntil);
  else if (f.state === FState.Crawl && mag <= T.MOVE.walkDead) { f.vel.x = 0; f.vel.z = 0; setState(f, FState.Lying, tick, f.lyingUntil); }
}

/** SimSystems.stepKnockdown — ARCHITECTURE §3 adım 2 (stepTimers sonrası; hitstop'ta atlanır). */
export function stepKnockdown(world: SimWorld, f: Fighter): void {
  if (f.hitstop > 0) return;
  const tick = world.tick;
  switch (f.state) {
    case FState.Hitstun:
      if (tick >= f.stateUntil) toIdleOrAir(f, tick);
      break;
    case FState.Launched:
      // iniş: kalan hitstun yerde sürer (juggle'daki küçük lift hitstun'ı kesmesin); bittiyse Idle (Jump_Land view'da)
      if (f.grounded) { if (tick < f.stateUntil) setState(f, FState.Hitstun, tick, f.stateUntil); else setState(f, FState.Idle, tick); break; }
      if (tick >= f.stateUntil) setState(f, FState.Air, tick);      // hitstun bitti: hava kontrolü (çift zıplama / hava dodge)
      break;
    case FState.Tumble:
      f.tumbleAngle += TUMBLE_STEP;
      // Takla yere değdi: süreyi TAKLA belirledi (normal 84 t / fırlatılmış 60 t — `enterTumble` yazdı), o yüzden
      // mevcut planı açıkça geçiriyoruz. Doğrudan knockdown yolunda (hits.ts) varsayılan taban kullanılır.
      if (f.grounded) { enterKnockdown(world, f, false, f.lyingPlan); freezeLastAttacker(world, f, T.HITSTOP.finisher); break; }
      if (!f.tumbleControl && tick >= f.stateUntil) {
        f.tumbleControl = true;
        world.buffers[f.id]!.pressTick[btnIndex(Btn.Jump)] = NO_TICK;   // tumble'da mash'lenen A çift zıplamayı boşa harcamasın
        world.events.push(Ev.TumbleControl, tick, f.id);
      }
      break;
    case FState.Knockdown:
      slide(f);
      if (tick >= f.stateUntil) enterLying(world, f, f.lyingPlan, f.faceDown);
      break;
    case FState.Lying:
    case FState.Crawl:
      if (f.state === FState.Lying) slide(f);
      if (tick >= f.lyingUntil) {
        // yüzüstü (Flying Kick whiff): otomatik hızlı kalk, sonrası i-frame yok; sırtüstü: GetUp 48 t + 36 t
        if (f.faceDown) enterGetUp(world, f, true, T.KNOCKDOWN.whiffPostGetUpIframes);
        else enterGetUp(world, f, false, T.KNOCKDOWN.postGetUpIframes);
        break;
      }
      lyingInputs(world, f);
      break;
    case FState.GetUp:
      if (tick >= f.stateUntil) { f.stun = 0; f.faceDown = false; setState(f, FState.Idle, tick); }
      break;
    default: break;
  }
}
